import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Breadcrumbs, Typography } from '@mui/material'
import { useState, useContext, useEffect, useRef } from 'react'

import '../styles/users-management.css'
import { Pocket } from '../utils/dexie/db'
import { countriesCurrencies } from '../lib'
import { useSocket } from '../hooks/use-socket'
import { FormError, SystemUser, UserSessionType } from '../types'
import Dialog from '../components/common/dialog'
import { requestAccessToken } from '../api/auth.api'
import { AuthContext } from '../contexts/auth.context'
import { UserContext } from '../contexts/user.context'
import Pagination from '../components/common/pagination'
import { handleIsValidMatch } from '../utils/common.utils'
import { AppStateContext } from '../contexts/app-state.context'
import ActionsBar from '../components/common/users-actions-bar'
import { getSystemUsersByCountry } from '../api/app-queries.api'
import { handleSyncDataToLocalDB } from '../utils/sync-data.utils'
import { normalizeSystemUserData } from '../utils/normalize-data.utils'
import { NotificationsContext } from '../contexts/notifications.context'
import { FiltersOptionsModal } from '../components/common/filters-options-modal'
import { resendActivationEmail, deleteSystemUserData } from '../api/system-users.api'
import { handleDeleteDataByOne, handleGetDataFromTableById } from '../utils/db/common-methods'
import SystemUserDataListItem from '../components/system-users-management/system-user-data-list-item'
import { UpdateSystemUserModal } from '../components/system-users-management/update-system-user-modal'
import { AddNewSystemUserModal } from '../components/system-users-management/add-new-system-user-modal'

const SystemUsersManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t, i18n } = useTranslation(['system-users-management', 'common'])
  const { setScrollToTop, updateLoadingStatus } = useContext(AppStateContext)

  const currentListPageRef = useRef<number>(1)
  const systemUserToSearchRef = useRef<string>('')
  const currentLoadingDataRef = useRef<boolean>(true)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const systemUsersToDeleteRef = useRef<Array<string>>([])
  const currentSystemUsersDataRef = useRef<Array<SystemUser>>([])
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const currentShowDeleteButtonFromActionsBarRef = useRef<boolean>(false)

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [userToSearch, setUserToSearch] = useState<string>('')
  const [hasMultipleRows, setHasMultipleRows] = useState(false)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [systemUserData, setSystemUserData] = useState<Partial<SystemUser>>({
    role: '',
    username: '',
    lastName: '',
    firstName: ''
  })
  const [systemUsersList, setSystemUsersList] = useState<Array<SystemUser>>([])
  const [systemUsersData, setSystemUsersData] = useState<Array<SystemUser>>([])
  const [currentPocketsData, setCurrentPocketsData] = useState<Array<Pocket>>([])
  const [selectAllSystemUsers, setSelectAllSystemUsers] = useState<boolean>(false)
  const [systemUsersToDelete, setSystemUsersToDelete] = useState<Array<string>>([])
  const [openNewSystemUserModal, setOpenNewSystemUserModal] = useState<boolean>(false)
  const [searchedSystemUsers, setSearchedSystemUsers] = useState<Array<SystemUser>>([])
  const [openFiltersOptionsModal, setOpenFiltersOptionsModal] = useState<boolean>(false)
  const [openDeleteSystemUserModal, setOpenDeleteSystemUserModal] = useState<boolean>(false)
  const [openUpdateSystemUserModal, setOpenUpdateSystemUserModal] = useState<boolean>(false)
  const [dialogDeleteUserIsLoading, setDialogDeleteUserIsLoading] = useState<boolean>(false)
  const [showDeleteButtonFromActionsBar, setShowDeleteButtonFromActionsBar] = useState<boolean>(false)
  const [systemUsersFromOtherCountries, setSystemUsersFromOtherCountries] = useState<Array<SystemUser>>([])

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('addCustomerCompanyPocket', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        handleGetSystemUsersByCountryFromRemote()
      }
    })

    appDataSocket.on('updateCustomerCompanyPocket', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        handleGetSystemUsersByCountryFromRemote()
      }
    })

    appDataSocket.on('deleteCustomerCompanyPocket', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        handleGetSystemUsersByCountryFromRemote()
      }
    })

    appDataSocket.on('addNewSystemUser', async (dataFromSocket: any) => {
      const belongsToThisCountry = currentPocketsData.some((pocketData) => dataFromSocket.adminOfPockets.includes(pocketData.id))

      if (belongsToThisCountry && String(dataFromSocket.belongsTo) === String(userSession?.belongsTo)) {
        handleGetSystemUsersByCountryFromRemote()
      }
    })

    appDataSocket.on('updateSystemUser', async (dataFromSocket: any) => {
      const belongsToThisCountry = currentPocketsData.some((pocketData) => dataFromSocket.adminOfPockets.includes(pocketData.id))

      if (belongsToThisCountry && String(dataFromSocket.belongsTo) === String(userSession?.belongsTo)) {
        handleGetSystemUsersByCountryFromRemote()
      }
    })

    appDataSocket.on('deleteSystemUser', async (dataFromSocket: any) => {
      const belongsToThisCountry = currentPocketsData.some((pocketData) => dataFromSocket.adminOfPockets.includes(pocketData.id))

      if (belongsToThisCountry && String(dataFromSocket.belongsTo) === String(userSession?.belongsTo)) {
        handleGetSystemUsersByCountryFromRemote()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, userSession, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetSystemUsersByCountry()
    handleDeleteSystemUserFromLocalDB('')

    const checkRows = () => {
      if (!containerRef.current) return

      const container = containerRef.current
      const firstItem = container.firstElementChild
      if (!firstItem) return

      const firstItemTop = firstItem.getBoundingClientRect().top
      const multipleRows = Array.from(container.children).some((item) => item.getBoundingClientRect().top > firstItemTop)

      setHasMultipleRows(multipleRows)
    }

    const resizeObserver = new ResizeObserver(checkRows)

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleSetUserToSearch('', false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPocketId])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const systemUsersPerPage = userToSearch === '' && selectedPocketId === '' ? systemUsersData.slice(firstPageIndex, lastPageIndex) : searchedSystemUsers.slice(firstPageIndex, lastPageIndex)

    setSystemUsersList(systemUsersPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, systemUsersData, searchedSystemUsers])

  const handleGetSystemUsersByCountry = () => {
    let localSystemUsersByCountryToSet: any = {}
    const customerCompanyId = String(userSession?.belongsTo)
    const localSystemUsersByCountry = globalThis.sessionStorage.getItem(`systemUsers:${customerCompanyId}:${selectedCountry}`)

    if (localSystemUsersByCountry) {
      localSystemUsersByCountryToSet = JSON.parse(localSystemUsersByCountry)
      handleSetSystemUsersByCountryInitialData(localSystemUsersByCountryToSet)
    }

    handleGetSystemUsersByCountryFromRemote()
  }

  const handleGetSystemUsersByCountryFromRemote = async () => {
    const customerCompanyId = String(userSession?.belongsTo)

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const { data } = await getSystemUsersByCountry(String(customerCompanyId), selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`systemUsers:${customerCompanyId}:${selectedCountry}`, JSON.stringify(data))
      handleSetSystemUsersByCountryInitialData(data)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleSetSystemUsersByCountryInitialData = (dataToSet: any) => {
    const systemUsersDataFromRemoteDb = [...dataToSet.systemUsersByCountry]

    const currentSelectedSystemUsersIds =
      currentSystemUsersDataRef.current.length === 0 ? [] : currentSystemUsersDataRef.current.filter((systemUserData) => systemUserData.selected === true).map((systemUserData) => String(systemUserData._id))
    const currentListPageDataState = currentListPageRef.current
    const currentSystemUserToSearchState = systemUserToSearchRef.current
    const systemUsersNormalizedData = []

    for (const element of systemUsersDataFromRemoteDb) {
      const systemUserData = { ...element }
      const systemUserNormalizedData = normalizeSystemUserData(systemUserData) as SystemUser

      if (currentSelectedSystemUsersIds.includes(String(systemUserNormalizedData._id))) {
        systemUserNormalizedData.selected = true
      } else {
        systemUserNormalizedData.selected = false
      }

      systemUsersNormalizedData.push(systemUserNormalizedData)
    }

    systemUsersNormalizedData.sort((thisSystemUser: Partial<SystemUser>, nextSystemUser: Partial<SystemUser>) => {
      const thisFirstName = thisSystemUser.firstName ? thisSystemUser.firstName.toLowerCase() : ''
      const nextFirstName = nextSystemUser.firstName ? nextSystemUser.firstName.toLowerCase() : ''

      return thisFirstName.localeCompare(nextFirstName)
    })

    if (currentSystemUserToSearchState === '' || currentSystemUserToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const systemUsersPerPage = systemUsersNormalizedData.slice(firstPageIndex, lastPageIndex)

      handleSystemUsersDeletionProcess(systemUsersPerPage)
      setSystemUsersList(systemUsersPerPage)

      for (const element of systemUsersPerPage) {
        const systemUserToAdd = normalizeSystemUserData(element)
        handleSyncDataToLocalDB('system-users', systemUserToAdd)
      }
    }

    currentSystemUsersDataRef.current = systemUsersNormalizedData
    setSystemUsersData(systemUsersNormalizedData)

    dataToSet.pocketsDataByCountry.sort((a: Pocket, b: Pocket) => String(a.name).localeCompare(String(b.name)))
    setCurrentPocketsData(dataToSet.pocketsDataByCountry)

    setSystemUsersFromOtherCountries(dataToSet.systemUsersByOtherCountries)

    if (openUpdateSystemUserModal) {
      const systemUserDataUpdating = systemUsersNormalizedData.find((systemUserNormalizedData) => String(systemUserNormalizedData._id) === String(systemUserData._id))

      if (systemUserDataUpdating) {
        setSystemUserData({
          role: systemUserDataUpdating.role,
          _id: String(systemUserDataUpdating._id),
          username: systemUserDataUpdating.username,
          lastName: systemUserDataUpdating.lastName,
          selected: systemUserDataUpdating.selected,
          createdAt: systemUserDataUpdating.createdAt,
          firstName: systemUserDataUpdating.firstName,
          activated: systemUserDataUpdating.activated,
          description: systemUserDataUpdating.description,
          adminOfPockets: systemUserDataUpdating.adminOfPockets
        })
      } else {
        setSystemUserData({
          role: '',
          username: '',
          lastName: '',
          firstName: ''
        })
        setOpenUpdateSystemUserModal(false)
      }
    }

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }
  }

  const handleDeleteSystemUserFromLocalDB = async (documentKey: string) => {
    const systemUserDataSetted: Array<SystemUser> = (await handleGetDataFromTableById('system-users', String(documentKey))) as Array<SystemUser>

    if (systemUserDataSetted.length > 0) {
      await handleDeleteDataByOne('system-users', documentKey)
    }
  }

  const handleCloseSession = () => {
    if (removeAuth) {
      removeAuth()
      navigate('/', { replace: true, state: { sessionExpired: true } })
    }
  }

  const handleAddSystemUserDataToTable = async (systemUserDataToAddToTable: SystemUser) => {
    const currentListPageDataState = currentListPageRef.current
    const currentSystemUserToSearchState = systemUserToSearchRef.current
    const currentSystemUsersDataState = currentSystemUsersDataRef.current ?? []

    const systemUserAlreadyAddedToTable = currentSystemUsersDataState.find((systemUserData) => String(systemUserData._id) === String(systemUserDataToAddToTable._id))

    if (!systemUserAlreadyAddedToTable) {
      const systemUsersDataToDisplayUpdated = currentSystemUsersDataState.concat(systemUserDataToAddToTable)
      currentSystemUsersDataRef.current = systemUsersDataToDisplayUpdated
      systemUsersDataToDisplayUpdated.sort((thisSystemUser: SystemUser, nextSystemUser: SystemUser) => (thisSystemUser?.firstName || '').toLowerCase().localeCompare((nextSystemUser?.firstName || '').toLowerCase()))

      if (currentSystemUserToSearchState === '' || currentSystemUserToSearchState === null) {
        const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
        const lastPageIndex = firstPageIndex + pageSize
        const systemUsersPerPage = systemUsersDataToDisplayUpdated.slice(firstPageIndex, lastPageIndex)

        handleSystemUsersDeletionProcess(systemUsersPerPage)
        setSystemUsersList(systemUsersPerPage)
      }

      setSystemUsersData(systemUsersDataToDisplayUpdated)
      return systemUsersDataToDisplayUpdated
    } else {
      return currentSystemUsersDataState
    }
  }

  const handleSystemUsersDeletionProcess = (systemUsersPerPage: Array<SystemUser>) => {
    const currentSystemUsersToDeleteState = systemUsersToDeleteRef.current ?? []
    const currentShowDeleteButtonFromActionsBarState = currentShowDeleteButtonFromActionsBarRef.current
    const systemUsersToDeleteOutOfPage = []

    for (const element of currentSystemUsersToDeleteState) {
      const systemUserToDeleteId = element
      const findSystemUserIndex = systemUsersPerPage?.findIndex((systemUserData) => String(systemUserData._id) === String(systemUserToDeleteId))

      if (findSystemUserIndex === -1) {
        systemUsersToDeleteOutOfPage.push(systemUserToDeleteId)
      }
    }

    for (const element of systemUsersToDeleteOutOfPage) {
      const systemUserId = element
      const index = currentSystemUsersToDeleteState.indexOf(systemUserId)
      if (index > -1) {
        currentSystemUsersToDeleteState.splice(index, 1)
      }
    }

    systemUsersToDeleteRef.current = currentSystemUsersToDeleteState
    if (systemUsersToDeleteRef.current.length < pageSize) {
      setSelectAllSystemUsers(false)
    }

    if (systemUsersToDeleteRef.current.length === 0) {
      setShowDeleteButtonFromActionsBar(false)
    } else {
      setShowDeleteButtonFromActionsBar(currentShowDeleteButtonFromActionsBarState)
    }

    setTimeout(() => {
      setSystemUsersToDelete(currentSystemUsersToDeleteState)
    }, 50)
  }

  const handleSetUserToSearch = (userToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    systemUserToSearchRef.current = userToSearchData

    setErrors({})
    setCurrentListPage(1)
    setUserToSearch(userToSearchData)

    const currentFullSystemUsersData = selectedPocketId
      ? [...currentSystemUsersDataRef.current].filter((systemUserData: SystemUser) => systemUserData.adminOfPockets?.includes(selectedPocketId))
      : [...currentSystemUsersDataRef.current]

    if (userToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentSystemUsersDataState = currentFullSystemUsersData ?? []
      const systemUsersPerPage = currentSystemUsersDataState.slice(firstPageIndex, lastPageIndex)

      setSystemUsersList(systemUsersPerPage)

      if (!userToSearchData && !selectedPocketId) setSearchedSystemUsers([])

      if (showError) setErrors({ userToSearch: t('searchField.errors.emptyField') })
      return
    }

    const filteredSystemtUsers = currentFullSystemUsersData.filter((systemUser) => {
      const systemUserActivationMode = systemUser.activated === true ? t('userStatus.active') : t('userStatus.inactive')
      const role =
        systemUser.role === 'admin' || (userSession?.role.includes('-client') && systemUser.role === 'admin-client')
          ? t('userRoles.administrator')
          : systemUser.role === 'admin-client' || (userSession?.role.includes('-client') && systemUser.role === 'super-admin-client')
            ? t('userRoles.clientAdmin')
            : systemUser.role === 'admin-client-builder' && userSession?.role.includes('-client')
              ? t('userRoles.clientBuilder')
              : t('userRoles.builder')

      const systemUserFullName = handleIsValidMatch(userToSearchData.toLowerCase(), String(`${systemUser.firstName} ${systemUser.lastName}`).toLowerCase())
      const systemUserDescription = handleIsValidMatch(userToSearchData.toLowerCase(), String(systemUser.description).toLowerCase())

      if (systemUser.selected === true) systemUser.selected = false

      return systemUserFullName || systemUserDescription || role.toLowerCase().includes(userToSearchData.toLowerCase()) || systemUserActivationMode.toLowerCase().includes(userToSearchData.toLowerCase())
    })

    systemUsersToDeleteRef.current = []
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const systemUsersPerPage = filteredSystemtUsers.slice(firstPageIndex, lastPageIndex)

    setSystemUsersToDelete([])
    setSelectAllSystemUsers(false)
    setSystemUsersList(systemUsersPerPage)
    setSearchedSystemUsers(filteredSystemtUsers)
  }

  const handleConfirmUserDeletion = async () => {
    setErrors({})
    setDialogDeleteUserIsLoading(true)
    const currentSystemUsersToDelete = [...systemUsersToDelete]

    for (const element of currentSystemUsersToDelete) {
      const systemUserToDeleteId = element

      try {
        const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
        currentShowDeleteButtonFromActionsBarRef.current = false
        setShowDeleteButtonFromActionsBar(false)
        await deleteSystemUserData(systemUserToDeleteId, accessTokenData.data.accessToken)
        // await handleDeleteDataByOne('system-users', systemUserToDeleteId)
      } catch (error: any) {
        console.log(error, error?.response?.data?.message)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        handleDiscardDeletionProcess()
        return
      }
    }

    handleDiscardDeletionProcess()
    setOpenDeleteSystemUserModal(false)
    updateNewNotificationStatus?.(true, 'system-users-deleted')
  }

  const handleDiscardDeletionProcess = () => {
    setSystemUsersToDelete([])
    systemUsersToDeleteRef.current = []
    setDialogDeleteUserIsLoading(false)
    const currentSystemUserToSearchState = systemUserToSearchRef.current
    setSystemUserData({
      role: '',
      username: '',
      lastName: '',
      firstName: ''
    })

    if (currentSystemUserToSearchState !== '') {
      handleSetUserToSearch('', false)
    }
  }

  const handleResendSystemUserInvitation = async (selectedSystemUserData: SystemUser) => {
    updateLoadingStatus?.(true)

    try {
      const systemUserDataToSend = {
        systemUserId: String(selectedSystemUserData._id),
        systemUserEmail: selectedSystemUserData.username,
        systemUserLanguage: i18n?.language.includes('es') ? 'es' : 'en'
      }

      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await resendActivationEmail(systemUserDataToSend, accessTokenData.data.accessToken)

      updateLoadingStatus?.(false)
      updateNewNotificationStatus?.(true, 'resend-email-notification-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else {
        updateLoadingStatus?.(false)
        updateNewNotificationStatus?.(true, 'resend-email-notification-error')
      }
    } finally {
      updateLoadingStatus?.(false)
    }
  }

  return (
    <div className="section-container">
      <Breadcrumbs aria-label="breadcrumb">
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.firstLevel')}
        </Typography>
      </Breadcrumbs>
      <ActionsBar
        errors={errors}
        processingDocument={false}
        userToSearch={userToSearch}
        userTypeName={t('userTypeName')}
        className="system-users-management"
        generalActionChecked={selectAllSystemUsers}
        handleSetUserToSearch={handleSetUserToSearch}
        handleClearSearchInput={() => setErrors({})}
        showDeleteButton={showDeleteButtonFromActionsBar}
        handleOpenFiltersOptionsModal={() => setOpenFiltersOptionsModal(true)}
        usersData={userToSearch === '' ? systemUsersData : searchedSystemUsers}
        handleDialogOpenState={(actionToDo: string) => {
          if (actionToDo === 'delete') {
            setOpenDeleteSystemUserModal(!openDeleteSystemUserModal)
            return
          }

          const firstPageIndex = (currentListPage - 1) * pageSize
          const lastPageIndex = firstPageIndex + pageSize
          const systemUsersPerPage = systemUsersData.slice(firstPageIndex, lastPageIndex)

          setSystemUsersList(systemUsersPerPage)
          setOpenNewSystemUserModal(!openNewSystemUserModal)
        }}
        handleToggleGeneralActionChecked={(checked) => {
          setSelectAllSystemUsers(checked)

          if (checked === true) {
            const currentSystemUsersList = [...systemUsersList]
            const currentSystemUsersDataStateUpdated = currentSystemUsersDataRef.current
            const systemUsersListUpdated = currentSystemUsersList.map((systemUserData) => ({
              ...systemUserData,
              selected: true
            }))
            const currentSystemUsersDataIds = systemUsersListUpdated.map((systemUserData) => systemUserData._id)
            const filteredSystemUsersToDelete = currentSystemUsersDataIds.filter((id) => id !== undefined) as Array<string>
            currentShowDeleteButtonFromActionsBarRef.current = true
            systemUsersToDeleteRef.current = filteredSystemUsersToDelete

            for (const element of systemUsersListUpdated) {
              const systemUserListData = element
              const findSystemUserIndex = currentSystemUsersDataStateUpdated?.findIndex((systemUserData) => String(systemUserData._id) === String(systemUserListData._id)) ?? -1

              if (findSystemUserIndex !== -1 && currentSystemUsersDataStateUpdated) {
                currentSystemUsersDataStateUpdated[findSystemUserIndex].selected = true
              }
            }

            currentSystemUsersDataRef.current = currentSystemUsersDataStateUpdated

            setSystemUsersList(systemUsersListUpdated)
            setShowDeleteButtonFromActionsBar(true)
            setSystemUsersToDelete(filteredSystemUsersToDelete)
            setSystemUsersData(currentSystemUsersDataStateUpdated)
          } else {
            const currentSystemUsersList = [...systemUsersList]
            const currentSystemUsersDataStateUpdated = currentSystemUsersDataRef.current
            const systemUsersListUpdated = currentSystemUsersList.map((systemUserData) => ({
              ...systemUserData,
              selected: false
            }))

            for (const element of systemUsersListUpdated) {
              const systemUserListData = element
              const findSystemUserIndex = currentSystemUsersDataStateUpdated?.findIndex((systemUserData) => String(systemUserData._id) === String(systemUserListData._id)) ?? -1

              if (findSystemUserIndex !== -1 && currentSystemUsersDataStateUpdated) {
                currentSystemUsersDataStateUpdated[findSystemUserIndex].selected = false
              }
            }

            systemUsersToDeleteRef.current = []
            currentShowDeleteButtonFromActionsBarRef.current = false
            currentSystemUsersDataRef.current = currentSystemUsersDataStateUpdated

            setSystemUsersToDelete([])
            setSystemUsersList(systemUsersListUpdated)
            setShowDeleteButtonFromActionsBar(false)
            setSystemUsersData(currentSystemUsersDataStateUpdated)
          }
        }}
      />
      {userSession && userSession?.role === 'super-admin-client' && Number(userSession?.adminOfPockets?.length) > 1 && currentPocketsData && currentPocketsData?.length > 1 && (
        <div
          ref={containerRef}
          className={`pockets-submenu-container ${hasMultipleRows ? 'has-multiple-rows' : ''}`}
        >
          <div
            onClick={() => setSelectedPocketId('')}
            className={`button-item ${String(selectedPocketId) === '' ? 'button-item-selected' : ''}`}
          >
            {t('filters.all')?.toUpperCase()}
          </div>
          {currentPocketsData.map((pocketData: Pocket, index: number) => {
            return (
              <div
                key={index}
                onClick={() => setSelectedPocketId(String(pocketData.id))}
                className={`button-item ${String(pocketData.id) === String(selectedPocketId) ? 'button-item-selected' : ''}`}
              >
                {pocketData.name?.toUpperCase()}
              </div>
            )
          })}
        </div>
      )}
      <div className="table-header-container">
        <div className="users-table-column users-table-width-35 table-header-text">{t('tableHeaders.name')}</div>
        <div className="users-table-column users-table-width-35 table-header-text display-none-inter display-none-sm">{t('tableHeaders.description')}</div>
        <div className="users-table-column users-table-width-25">
          <div className="users-table-column users-table-width-40 table-header-text display-none-md">{t('tableHeaders.role')}</div>
          <div className="users-table-column users-table-width-30 table-header-text display-none-bg">{t('tableHeaders.accountStatus')}</div>
          <div className="users-table-column users-table-width-30 table-header-text">{t('tableHeaders.actions')}</div>
        </div>
      </div>
      {loadingLocalData ? (
        <div className="users-management-users-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="users-management-users-list-container">
            {systemUsersList.map((systemUserData: SystemUser) => {
              return (
                <SystemUserDataListItem
                  key={systemUserData._id}
                  userSession={userSession as UserSessionType}
                  systemUserData={systemUserData}
                  handleResendSystemUserInvitation={handleResendSystemUserInvitation}
                  handleOpenDeleteSystemUserDataModal={(selectedSystemUserData: SystemUser) => {
                    const systemUsersToDeleteUpdated = [selectedSystemUserData._id] as string[]
                    const currentSystemUsersList = [...systemUsersList]
                    const systemUsersListUpdated = currentSystemUsersList.map((systemUserData) => ({
                      ...systemUserData,
                      selected: false
                    }))

                    setSystemUserData({
                      ...selectedSystemUserData
                    })
                    setSelectAllSystemUsers(false)
                    setOpenDeleteSystemUserModal(true)
                    setShowDeleteButtonFromActionsBar(false)
                    setSystemUsersList(systemUsersListUpdated)
                    setSystemUsersToDelete(systemUsersToDeleteUpdated)
                  }}
                  handleOpenUpdateSystemUserDataModal={(selectedSystemUserData: SystemUser) => {
                    setSystemUserData({
                      ...selectedSystemUserData
                    })
                    handleSetUserToSearch('', false)
                    setOpenUpdateSystemUserModal(true)
                  }}
                  handleToggleSystemUserSelection={(selectedSystemUserData: SystemUser, selected: boolean) => {
                    let systemUsersSelected = 0
                    let systemUsersToDeleteUpdated = []
                    const currentSystemUsersList = [...systemUsersList]
                    const currentSystemUsersToDelete = [...systemUsersToDelete]
                    const currentSystemUsersDataStateUpdated = currentSystemUsersDataRef.current

                    const systemUsersListUpdated = currentSystemUsersList.map((systemUserData) => {
                      if (String(systemUserData._id) === String(selectedSystemUserData._id)) {
                        systemUserData.selected = selected
                      }

                      if (systemUserData.selected === true) systemUsersSelected++

                      return systemUserData
                    })

                    if (selected === true) {
                      systemUsersToDeleteUpdated = currentSystemUsersToDelete.concat(selectedSystemUserData._id ?? [])
                      systemUsersToDeleteRef.current = systemUsersToDeleteUpdated
                      setSystemUsersToDelete(systemUsersToDeleteUpdated)

                      const findSystemUserIndex = currentSystemUsersDataStateUpdated?.findIndex((systemUserData) => String(systemUserData._id) === String(selectedSystemUserData._id)) ?? -1

                      if (findSystemUserIndex !== -1 && currentSystemUsersDataStateUpdated) {
                        currentSystemUsersDataStateUpdated[findSystemUserIndex].selected = true
                      }

                      currentSystemUsersDataRef.current = currentSystemUsersDataStateUpdated
                      setSystemUsersData(currentSystemUsersDataStateUpdated)
                    } else {
                      const index = currentSystemUsersToDelete.indexOf(selectedSystemUserData._id ?? '')
                      if (index > -1) {
                        currentSystemUsersToDelete.splice(index, 1)
                      }

                      systemUsersToDeleteUpdated = [...currentSystemUsersToDelete]
                      systemUsersToDeleteRef.current = systemUsersToDeleteUpdated
                      setSystemUsersToDelete(currentSystemUsersToDelete)

                      const findSystemUserIndex = currentSystemUsersDataStateUpdated?.findIndex((systemUserData) => String(systemUserData._id) === String(selectedSystemUserData._id)) ?? -1

                      if (findSystemUserIndex !== -1 && currentSystemUsersDataStateUpdated) {
                        currentSystemUsersDataStateUpdated[findSystemUserIndex].selected = false
                      }

                      currentSystemUsersDataRef.current = currentSystemUsersDataStateUpdated
                      setSystemUsersData(currentSystemUsersDataStateUpdated)
                    }

                    if (systemUsersToDeleteUpdated.length > 0) {
                      currentShowDeleteButtonFromActionsBarRef.current = true
                      setShowDeleteButtonFromActionsBar(true)
                    } else {
                      currentShowDeleteButtonFromActionsBarRef.current = false
                      setShowDeleteButtonFromActionsBar(false)
                    }

                    if (systemUsersList.length === systemUsersSelected) {
                      setSelectAllSystemUsers(true)
                    } else {
                      setSelectAllSystemUsers(false)
                    }

                    setSystemUsersList(systemUsersListUpdated)
                  }}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              setScrollToTop(true)
              systemUsersToDeleteRef.current = []
              setSystemUsersToDelete([])
              setSelectAllSystemUsers(false)
              currentListPageRef.current = page
              setCurrentListPage(page)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={userToSearch === '' ? systemUsersData.length : searchedSystemUsers.length}
          />
          {openNewSystemUserModal && (
            <AddNewSystemUserModal
              pockets={currentPocketsData}
              systemUsersData={systemUsersData}
              disableSystemUserRoleSelect={false}
              handleCloseSession={handleCloseSession}
              handleResetPocketFilterData={() => setSelectedPocketId('')}
              systemUsersFromOtherCountries={systemUsersFromOtherCountries}
              handleAddSystemUserDataToTable={handleAddSystemUserDataToTable}
              handleOpenStateOfModal={(openState: boolean) => setOpenNewSystemUserModal(openState)}
            />
          )}
          {openUpdateSystemUserModal && (
            <UpdateSystemUserModal
              pockets={currentPocketsData}
              systemUserData={systemUserData}
              systemUsersData={systemUsersData}
              handleCloseSession={handleCloseSession}
              handleResetPocketFilterData={() => setSelectedPocketId('')}
              handleOpenStateOfModal={(openState: boolean) => setOpenUpdateSystemUserModal(openState)}
            />
          )}
          {openDeleteSystemUserModal && (
            <Dialog
              errors={errors}
              actionButtonWarningStyle
              actionButtonText={t('deleteSystemUserModal.actionButtonText')}
              dialogIsLoading={dialogDeleteUserIsLoading}
              handleDialogAction={handleConfirmUserDeletion}
              dialogTitle={systemUsersToDelete.length === 1 ? t('deleteSystemUserModal.title') : `${t('deleteSystemUserModal.title')}s`}
              handleDialogOpenState={(openState: boolean) => {
                setOpenDeleteSystemUserModal(openState)

                if (openState === false) {
                  setErrors({})
                  setSystemUserData({
                    role: '',
                    username: '',
                    lastName: '',
                    firstName: ''
                  })
                }
              }}
            >
              <div className="dialog-content-text">
                {systemUserData.firstName
                  ? t('deleteSystemUserModal.simpleUserText', { firstName: systemUserData.firstName.toUpperCase(), lastName: systemUserData.lastName?.toUpperCase() })
                  : t('deleteSystemUserModal.multipleUseText')}
              </div>
            </Dialog>
          )}
        </>
      )}
      {openFiltersOptionsModal && (
        <FiltersOptionsModal
          filtersToApply="systemUsers"
          systemUsersData={systemUsersData}
          handleDialogOpenState={(openState: boolean) => {
            setOpenFiltersOptionsModal(openState)
          }}
          title={t('filtersOptionsModal.systemUsers.title', { ns: 'common' })}
          actionButtonText={t('filtersOptionsModal.systemUsers.actionButtonText', { ns: 'common' })}
        />
      )}
    </div>
  )
}

export default SystemUsersManagement
